import * as EmailValidator from 'email-validator'
import React, {useReducer} from 'react'

import type {PrismicLinkFragment} from '@/graphql-types'
import useParams from '@/hooks/use-params'
import {scrollToTop} from '@/utils/scroll'
import Button from '@helios/shared/components/button'
import Checkbox from '@helios/shared/components/checkbox'
import Input from '@helios/shared/components/input'
import RichText from '@helios/shared/components/rich-text'
import useFormErrors from '@helios/shared/hooks/use-form-errors'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useRecaptchaHubspotSubmit} from '@helios/shared/hooks/use-recaptcha-hubspot-submit'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useGoogleReCaptcha} from '@helios/shared/providers/google-recaptcha'
import {Text} from '@helios/shared/styles/typography'
import type {HubspotContactProps} from '@helios/shared/types/hubspot-contact-props'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'

import * as s from '../lead-form.styles'

export const OTHER = 'Other' as const

export interface State {
    firstName: string
    lastName: string
    email: string
    companyName: string
    hasConsented: boolean
}

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const useValidateInputs = () => {
    const globalStrings = useGlobalStrings()

    return (state: State) => {
        type Error = {
            key: keyof State
            value: string
        }

        const errors: Error[] = []

        const isValidEmail = EmailValidator.validate(state.email)

        if (!state.firstName.trim()) {
            errors.push({
                key: 'firstName',
                value: globalStrings.this_field_is_required,
            })
        }

        if (!state.lastName.trim()) {
            errors.push({
                key: 'lastName',
                value: globalStrings.this_field_is_required,
            })
        }

        if (!state.email.trim() || !isValidEmail) {
            errors.push({
                key: 'email',
                value: globalStrings.this_field_is_required,
            })
        }

        if (!state.companyName.trim()) {
            errors.push({
                key: 'companyName',
                value: globalStrings.this_field_is_required,
            })
        }

        if (!state.hasConsented) {
            errors.push({
                key: 'hasConsented',
                value: globalStrings.this_field_is_required,
            })
        }

        return errors
    }
}

type Props = {
    title: string
    confirmationPage: PrismicLinkFragment
    hubspotProperties?: Partial<HubspotContactProps>
}

export const PodcastForm = ({title, confirmationPage, hubspotProperties}: Props) => {
    const params = useParams()
    const {recaptchaHubspotSubmit, loading} = useRecaptchaHubspotSubmit()
    const globalStrings = useGlobalStrings()
    const validateInputs = useValidateInputs()
    const {loadReCaptcha} = useGoogleReCaptcha()

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Validate inputs
        const errors = validateInputs(state)

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })

            return scrollToTop()
        }

        await recaptchaHubspotSubmit(
            {
                firstName: state.firstName,
                lastName: state.lastName,
                email: state.email,
                company: state.companyName,
            },
            hubspotProperties
        )

        track(TrackingEvent.PodcastFormActioned, {
            action: 'submitted',
            email: state.email,
        })

        // Redirect user
        return navigate(`/${confirmationPage.uid}`, {
            state: {
                email: state.email,
            },
        })
    }

    const initialState: State = {
        firstName: '',
        lastName: '',
        email: params.get('email') || '',
        companyName: '',
        hasConsented: false,
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)
    const navigate = useNavigate()

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })
    }

    const handleInputChange: typeof setValue = (key, value) => {
        loadReCaptcha()
        clearError(key)
        setValue(key, value)
    }

    const errorSpacePlaceholder = <Text variant="label-base">{'\u00a0'}</Text>

    return (
        <s.Form onSubmit={handleFormSubmit} noValidate>
            <Text variant="title-base" center>
                {title}
            </Text>

            <s.InputsWrapper>
                <s.Row>
                    <div>
                        <Input
                            label={globalStrings.first_name}
                            value={state.firstName}
                            onChange={(value) => handleInputChange('firstName', value)}
                            error={errors.firstName}
                            required
                        />

                        {!errors.firstName && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Input
                            label={globalStrings.last_name}
                            value={state.lastName}
                            onChange={(value) => handleInputChange('lastName', value)}
                            error={errors.lastName}
                            required
                        />

                        {!errors.lastName && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Input
                            type="email"
                            label={globalStrings.work_email}
                            value={state.email}
                            onChange={(value) => handleInputChange('email', value)}
                            error={errors.email}
                            required
                        />

                        {!errors.email && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Input
                            label={globalStrings.company_name}
                            value={state.companyName}
                            onChange={(value) => handleInputChange('companyName', value)}
                            error={errors.companyName}
                            required
                        />

                        {!errors.companyName && errorSpacePlaceholder}
                    </div>
                </s.Row>
            </s.InputsWrapper>

            <Checkbox
                label={globalStrings.accept_terms_and_privacy}
                checked={state.hasConsented}
                onChange={(value) => handleInputChange('hasConsented', value)}
                error={errors.hasConsented}
            />

            <Button loading={loading}>{globalStrings.signUp}</Button>

            <RichText
                variant="label-base"
                color="muted"
                content={globalStrings.recaptcha_message}
            />
        </s.Form>
    )
}
